import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Snackbar, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import styled from 'styled-components';

import coreAPI from '../../apis/core';
import Switch from '../Shared/Switch';
import { HealtheraButton } from '../../healtheraComponents';
import DynamicDialog from '../DynamicDialog/DynamicDialog';
import OrganisationPlanSelect from '../Shared/OrganisationPlanSelect';
import { organisationPlanLabel } from '../Shared/organisationPlans';
import { mergeFeatureFlagCatalogue } from '../FeatureFlags/featureFlagCatalogue';
import { updateOrganisation, getOrganisation, assertSucceeded } from '../../redux/organisations';
import FeatureOverrideSelect from './FeatureOverrideSelect';
import { featureFlagChoice, featureFlagUpdate, planFeatureFlags } from './planFeatureFlags';

const CHOICE_LABELS = { plan: 'Plan default', on: 'On', off: 'Off' };

const sortAlphabetically = (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });

const resultOf = (choice, onInPlan) => (choice === 'plan' ? onInPlan : choice === 'on');

export default function OrganisationPlan() {
  const dispatch = useDispatch();
  const organisation = useSelector(({ organisations }) => organisations.organisation);
  const [catalogue, setCatalogue] = useState([]);
  const [organisationFeatures, setOrganisationFeatures] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingChange, setPendingChange] = useState(null);
  const [saving, setSaving] = useState(false);

  const organisationId = organisation?._id;
  const organisationPlan = organisation?.plan;

  useEffect(() => {
    (async () => {
      try {
        setCatalogue(mergeFeatureFlagCatalogue(await coreAPI.getFeatureFlags()));
      } catch (error) {
        setError(error.message);
      }
    })();
  }, []);

  useEffect(() => {
    if (!organisationId) return;
    let active = true;
    setOrganisationFeatures(null);
    (async () => {
      try {
        const features = await coreAPI.getOrganisationFeatures(organisationId);
        if (active) setOrganisationFeatures(features);
      } catch (error) {
        if (active) setError(`Could not load the plan features: ${error.message}`);
      }
    })();
    return () => {
      active = false;
    };
  }, [organisationId, organisationPlan]);

  const planFlags = useMemo(
    () =>
      organisationFeatures
        ? planFeatureFlags(organisationFeatures)
            .map(flag => ({ ...flag, title: catalogue.find(f => f._id === flag.key)?.title ?? flag.key }))
            .sort(sortAlphabetically)
        : [],
    [organisationFeatures, catalogue]
  );

  if (!organisation) return null;

  const confirmChange = async () => {
    const { update, successMessage: message } = pendingChange;
    setSaving(true);
    try {
      assertSucceeded(await dispatch(updateOrganisation(organisation._id, update)));
      await dispatch(getOrganisation(organisation._id));
      setSuccessMessage(message);
    } catch (err) {
      setError('Failed to update the organisation');
    }
    setSaving(false);
    setPendingChange(null);
  };

  const onPlanChange = plan =>
    setPendingChange({
      title: `Move this organisation to the ${organisationPlanLabel(plan)} plan?`,
      contentText: "Connect shows the new plan's features after a reload. Overrides stay as they are.",
      update: { plan },
      successMessage: `Plan changed to ${organisationPlanLabel(plan)}`,
    });

  const onOverrideChange = (flag, choice) =>
    setPendingChange({
      title: `Are you sure you want to set "${flag.title}" to ${CHOICE_LABELS[choice]}?`,
      update: featureFlagUpdate(flag.key, choice),
      successMessage: `"${flag.title}" has been set to ${CHOICE_LABELS[choice]}`,
    });

  const onProChange = enabled =>
    setPendingChange({
      title: `Are you sure you want to help the Organisation toggle ${enabled ? 'on' : 'off'} Healthera Pro?`,
      update: { pro: { enabled } },
      successMessage: `Healthera Pro has been turned ${enabled ? 'on' : 'off'}`,
    });

  const onBillingChange = async billing => {
    try {
      assertSucceeded(await dispatch(updateOrganisation(organisation._id, { pro: { billing } })));
      await dispatch(getOrganisation(organisation._id));
    } catch (err) {
      setError('Failed to update the organisation');
    }
  };

  return (
    <div>
      <Snackbar
        open={!!error}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        message={error}
      />
      <Snackbar
        open={!!successMessage}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        message={successMessage}
      />
      <DynamicDialog
        open={!!pendingChange}
        title={pendingChange?.title}
        contentText={pendingChange?.contentText}
        actions={
          <>
            <HealtheraButton
              onClick={confirmChange}
              disabled={saving}
              margin={'0 10px 5px'}
              height={'32px'}
              width={'95px'}
            >
              Confirm
            </HealtheraButton>
            <HealtheraButton onClick={() => setPendingChange(null)} margin={'0 10px 5px'} height={'32px'} width={'95px'}>
              Cancel
            </HealtheraButton>
          </>
        }
      />
      <h1 style={{ textAlign: 'center' }}>{organisation.name}</h1>
      <Section>
        <OrganisationPlanSelect plan={organisation.plan} onChange={onPlanChange} />
        {!organisation.plan && (
          <Hint>
            Not set — Connect treats it as{' '}
            {organisationFeatures ? organisationPlanLabel(organisationFeatures.plan) : 'Legacy / Legacy Pro'}
          </Hint>
        )}
      </Section>
      <Section>
        <SectionTitle>Plan features</SectionTitle>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Feature</TableCell>
              <TableCell>In plan</TableCell>
              <TableCell>Override</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {planFlags.map(flag => {
              const choice = featureFlagChoice(organisation.feature_flags, flag.key);
              const overridden = choice !== 'plan';
              return (
                <OverridableRow key={flag.key} $overridden={overridden}>
                  <TableCell>{flag.title}</TableCell>
                  <TableCell data-testid="in-plan">{flag.onInPlan ? '✓' : '–'}</TableCell>
                  <TableCell>
                    <FeatureOverrideSelect
                      label={flag.title}
                      choice={choice}
                      onInPlan={flag.onInPlan}
                      onChange={newChoice => onOverrideChange(flag, newChoice)}
                    />
                    {overridden && <OverrideTag>Override</OverrideTag>}
                  </TableCell>
                  <TableCell data-testid="result">{resultOf(choice, flag.onInPlan) ? 'On' : 'Off'}</TableCell>
                </OverridableRow>
              );
            })}
          </TableBody>
        </Table>
      </Section>
      <Section>
        <SectionTitle>Legacy Pro billing</SectionTitle>
        <Switch label="Healthera Pro" onChange={(_, value) => onProChange(value)} checked={!!organisation.pro?.enabled} />
        <Switch
          label="Billing"
          onChange={(_, value) => onBillingChange(value)}
          checked={!!organisation.pro?.billing}
          disabled={organisation.pro?.enabled !== true}
        />
        <Hint>Item fees are charged only when both are on.</Hint>
      </Section>
    </div>
  );
}

const Section = styled.div`
  margin: 1.5rem auto;
  max-width: 900px;
  padding: 0 16px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.5rem;
`;

const Hint = styled.p`
  color: rgba(0, 0, 0, 0.6);
  margin: 0.5rem 0 0;
`;

const OverridableRow = styled(TableRow)`
  && {
    background-color: ${props => (props.$overridden ? '#fff8e1' : 'transparent')};
  }
`;

const OverrideTag = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #b26a00;
`;
